/**
 * Feedback Scheduler for managing when to show emoji voting prompts
 * Shows feedback prompts every 2 weeks for 6 weeks total (3 prompts max)
 */

export class FeedbackScheduler {
  constructor() {
    this.FEEDBACK_INTERVAL_DAYS = 14; // Every 2 weeks
    this.TOTAL_PROGRAM_WEEKS = 6;
    this.MAX_PROMPTS = 3; // 6 weeks / 2 = 3 prompts
    this.STORAGE_KEY = 'layersFeedbackSchedule';
  }

  /**
   * Get the current feedback state for a user
   * @returns {Object} Feedback state object
   */
  getFeedbackState() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        // Initialize new user
        const state = {
          programStartDate: new Date().toISOString(),
          feedbackCount: 0,
          lastFeedbackDate: null,
          feedbackHistory: []
        };
        this.saveFeedbackState(state);
        return state;
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading feedback state:', error);
      return this.getFeedbackState(); // Reinitialize on error
    }
  }

  /**
   * Save feedback state to localStorage
   * @param {Object} state - Feedback state to save
   */
  saveFeedbackState(state) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving feedback state:', error);
    }
  }

  /**
   * Check if feedback prompt should be shown
   * @returns {boolean} Whether to show feedback prompt
   */
  shouldShowFeedback() {
    const state = this.getFeedbackState();
    const now = new Date();
    const startDate = new Date(state.programStartDate);
    
    // Calculate days since program start
    const daysSinceStart = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    
    // Don't show if we've reached max prompts
    if (state.feedbackCount >= this.MAX_PROMPTS) {
      return false;
    }
    
    // Don't show if we're past the 6-week window
    if (daysSinceStart > this.TOTAL_PROGRAM_WEEKS * 7) {
      return false;
    }
    
    // Show if it's been 2 weeks since last feedback (or first time)
    if (!state.lastFeedbackDate) {
      return daysSinceStart >= this.FEEDBACK_INTERVAL_DAYS;
    }
    
    const lastFeedback = new Date(state.lastFeedbackDate);
    const daysSinceLastFeedback = Math.floor((now - lastFeedback) / (1000 * 60 * 60 * 24));
    
    return daysSinceLastFeedback >= this.FEEDBACK_INTERVAL_DAYS;
  }

  /**
   * Record that feedback was submitted
   * @param {string} vote - The vote submitted ('too_cold', 'just_right', 'too_warm')
   * @param {Object} context - Additional context about the feedback
   */
  recordFeedbackSubmitted(vote, context = {}) {
    const state = this.getFeedbackState();
    const now = new Date();
    
    const feedbackEntry = {
      date: now.toISOString(),
      vote,
      context,
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    // Update state
    state.feedbackCount += 1;
    state.lastFeedbackDate = now.toISOString();
    state.feedbackHistory.push(feedbackEntry);
    
    this.saveFeedbackState(state);
    
    return state;
  }

  /**
   * Get the next scheduled feedback date
   * @returns {Date|null} Next feedback date or null if program is complete
   */
  getNextFeedbackDate() {
    const state = this.getFeedbackState();
    
    if (state.feedbackCount >= this.MAX_PROMPTS) {
      return null; // Program complete
    }
    
    const baseDate = state.lastFeedbackDate 
      ? new Date(state.lastFeedbackDate)
      : new Date(state.programStartDate);
    
    return new Date(baseDate.getTime() + (this.FEEDBACK_INTERVAL_DAYS * 24 * 60 * 60 * 1000));
  }

  /**
   * Get feedback progress information
   * @returns {Object} Progress information
   */
  getFeedbackProgress() {
    const state = this.getFeedbackState();
    const nextDate = this.getNextFeedbackDate();
    
    return {
      currentPrompt: state.feedbackCount + 1,
      totalPrompts: this.MAX_PROMPTS,
      feedbackCount: state.feedbackCount,
      nextFeedbackDate: nextDate,
      isComplete: state.feedbackCount >= this.MAX_PROMPTS,
      daysInProgram: Math.floor(
        (new Date() - new Date(state.programStartDate)) / (1000 * 60 * 60 * 24)
      )
    };
  }

  /**
   * Reset the feedback schedule (for testing or user request)
   */
  resetSchedule() {
    localStorage.removeItem(this.STORAGE_KEY);
    return this.getFeedbackState(); // This will create a new schedule
  }

  /**
   * Check if user has provided any feedback yet
   * @returns {boolean} Whether user has submitted feedback before
   */
  hasProvidedFeedback() {
    const state = this.getFeedbackState();
    return state.feedbackCount > 0;
  }
}

// Export singleton instance
export const feedbackScheduler = new FeedbackScheduler();